import { shallowCompare } from '@fluentui/react/lib/Utilities';
const murmur3 = require('murmurhash-js/murmurhash3_gc');

/**
 * Memoizes a function; Instead of returning a cached result when passing the same argument references, it returns
 * a cached result when the last and new result share the same hash. The cache only saves the previous result so its not suitable
 * if it will be called several times per store update.
 *
 * @param func The function to be memoize
 */
export function memoizeWithResultCheck<T extends (...args: any[]) => RET_TYPE, RET_TYPE>(func: T): T {
  let lastResult: RET_TYPE;
  let lastResultHash: string | null = null;
  let lastArgs: any[] | null = null;
  return function memoizeFunction(...args: any[]): RET_TYPE {
    if (argumentsShallowlyEqual(lastArgs, args)) {
      return lastResult;
    }

    const result = func(...args);
    // Use murmur3 to reduce the bytes needed to be stored in memory.
    const resultHash = murmur3(JSON.stringify(result)).toString(36);

    if (lastResult && lastResultHash === resultHash) {
      return lastResult;
    }

    lastResult = result;
    lastResultHash = resultHash;
    lastArgs = args;

    return result;
  } as T;
}

function argumentsShallowlyEqual(lastArgs: any[] | null, args: any[] | null): boolean {
  if (lastArgs === null || args === null || lastArgs.length !== args.length) {
    return false;
  }
  return shallowCompare(lastArgs, args);
}
